import { useClubApi } from "../../lib/clubScope";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, type ClubReservationInfo } from "../../lib/api";
import { useAbortableConfirm } from "../../lib/useAbortableConfirm";
import { formatDate } from "../../lib/dateFormat";

interface Props {
  token: string;
  onMessage: (message: string) => void;
  onChanged: () => void;
}

export default function ClubReservationsPanel({ token, onMessage, onChanged }: Props) {
  const clubApi = useClubApi();
  const { askConfirm } = useAbortableConfirm([clubApi]);
  const [pending, setPending] = useState<ClubReservationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await clubApi.pendingReservations(token);
      setPending(resp.data.reservations);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos cargar las reservas por aprobar.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(reservation: ClubReservationInfo, action: "approve" | "reject") {
    if (
      action === "reject" &&
      !(await askConfirm("¿Rechazar esta reserva? El horario quedará libre otra vez.", {
        acceptLabel: "Sí, rechazar reserva",
        tone: "danger",
      }))
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (action === "approve") {
        await clubApi.approveReservation(token, reservation.id);
        onMessage("Reserva aprobada.");
      } else {
        await clubApi.rejectReservation(token, reservation.id);
        onMessage("Reserva rechazada.");
      }
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo actualizar la reserva.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="club-panel__card" aria-label="Reservas">
        <div className="club-panel__card-head">
          <h2>Reservas</h2>
          <Link className="club-panel__lime-btn" to="/admin/calendar?modo=reservar">
            + Nueva reserva
          </Link>
        </div>
        <p className="club-panel__muted">
          Para ver la agenda completa, reservar por un socio o invitado, bloquear canchas o cancelar, abre el
          programador de canchas.
        </p>
        <div>
          <Link className="btn btn--ghost" to="/admin/calendar">
            Abrir programador de canchas →
          </Link>
        </div>
      </section>

      <section className="club-panel__card" aria-label="Reservas por aprobar">
        <div className="club-panel__card-head">
          <h2>Por aprobar · {pending.length}</h2>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        {loading ? (
          <p className="club-panel__muted">Cargando…</p>
        ) : pending.length === 0 ? (
          <p className="club-panel__muted">
            No hay reservas por aprobar. Si en Configuración eliges «El club aprueba cada una», aquí aparecerán las
            reservas de tus socios.
          </p>
        ) : (
          <div className="club-list">
            {pending.map((reservation) => (
              <div className="club-list__item" key={reservation.id}>
                <span>
                  <strong>{reservation.member_name ?? reservation.guest_names ?? "Socio"}</strong>
                  <small>
                    {reservation.court_name} · {formatDate(reservation.date, "Sin fecha")} · {reservation.start_time}–
                    {reservation.end_time}
                  </small>
                </span>
                <span className="club-list__actions">
                  <button type="button" className="btn btn--compact" disabled={busy} onClick={() => decide(reservation, "approve")}>
                    Aprobar
                  </button>
                  <button
                    type="button"
                    className="btn btn--compact btn--ghost"
                    disabled={busy}
                    onClick={() => decide(reservation, "reject")}
                  >
                    Rechazar
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
